import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import {
  Activity,
  Eye,
  CheckCircle,
  BarChart3,
  Bell,
  Gauge,
  Brain,
  FileSearch,
  TrendingUp,
  Users,
  ShieldCheck,
  Zap,
} from 'lucide-react';

/* ================================================================== *
 *  SOFTWARE SOLUTIONS — /software-solutions
 *  Sales-facing showcase for the two platforms EnergiPro built
 *  in-house. Every visitor here is a prospective customer, not an
 *  internal user — copy and CTAs are written for that audience.
 * ================================================================== */

const products = [
  {
    key: 'asset-tracking',
    name: 'EnergiPro Asset Tracking',
    tagline: 'Fleet & Rental Management, Built for Oilfield Operators',
    icon: <Activity className="w-7 h-7" />,
    pitch:
      "Stop chasing spreadsheets to find out what's on rent, what's overdue, and what's sitting idle in the yard. EnergiPro Asset Tracking gives you one live view of every asset, every rental, and every maintenance cycle across all your yards — the same system we run our own fleet on every day.",
    outcomes: [
      { icon: <Gauge className="w-5 h-5" />, title: 'Real-Time Utilization', body: 'See exactly which assets are on rent, idle, or in maintenance at any moment — across every yard, not just one.' },
      { icon: <Bell className="w-5 h-5" />, title: 'Overdue-Rental Alerts', body: 'Automatic alerts before a rental runs past its return date, so revenue leakage gets caught before it becomes a write-off.' },
      { icon: <BarChart3 className="w-5 h-5" />, title: 'Utilization Dashboards', body: 'Fleet performance reporting that shows which assets earn their keep and which are dead weight on the balance sheet.' },
      { icon: <ShieldCheck className="w-5 h-5" />, title: 'Maintenance Cycle Tracking', body: 'Every service, inspection and certification logged against the asset it belongs to — audit-ready, always.' },
    ],
    demoUrl: 'https://asset-tracking.energiprogroup.com',
  },
  {
    key: 'eip',
    name: 'EnergiPro Intelligence Platform (EIP)',
    tagline: 'AI-Powered Sales & Technical Intelligence',
    icon: <Brain className="w-7 h-7" />,
    pitch:
      "Drilling data reports contain the answer to your next sale — most teams just don't have time to read them. EIP turns that raw technical data into evidence-backed opportunity insights, revenue forecasts, and competitor intelligence, automatically, so your technical sales team walks into every conversation already ahead.",
    outcomes: [
      { icon: <FileSearch className="w-5 h-5" />, title: 'Automated Report Analysis', body: 'Drilling data reports parsed and turned into structured, evidence-backed insight — no manual review required.' },
      { icon: <TrendingUp className="w-5 h-5" />, title: 'Revenue Forecasting', body: 'Opportunity value and timing projected from real field data, not gut feel or a stale spreadsheet.' },
      { icon: <Users className="w-5 h-5" />, title: 'Competitor Intelligence', body: 'See where competitors are active and where the gaps are, so your team targets the right accounts first.' },
      { icon: <Zap className="w-5 h-5" />, title: 'Built for Technical Sales', body: 'Insights delivered in language a technical sales team can act on immediately — not a generic BI dashboard.' },
    ],
    demoUrl: 'https://intelligence.energiprogroup.com',
  },
];

export default function SoftwareSolutions() {
  return (
    <Layout>
      <SeoHead
        title="Software Solutions | EnergiPro Group — Asset Tracking & Sales Intelligence Platforms"
        description="EnergiPro Group's software solutions: EnergiPro Asset Tracking for fleet and rental management, and the EnergiPro Intelligence Platform (EIP) for AI-powered sales intelligence. Try live demos free."
        canonicalPath="/software-solutions"
      />

      {/* ---------- HERO ---------- */}
      <section className="relative bg-slate-950 text-white pt-32 pb-20 md:pt-36 md:pb-24 border-b-2 border-orange-600 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_20%_20%,#ea580c_0,transparent_45%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider mb-6">
            <Zap size={14} /> Proprietary Software
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase text-white leading-[1.05] max-w-4xl">
            Software That Runs Our Operations — Now Available to Run Yours
          </h1>
          <p className="mt-7 max-w-3xl text-slate-200 text-base md:text-lg leading-relaxed">
            We didn't build these platforms as a side project — we built them because we needed them
            to run our own fleet and win our own deals. They've been tested against real operational
            pressure, every day, across our own yards and sales team. Now they're available to any
            operator or service company that wants the same edge.
          </p>
          <p className="mt-4 max-w-3xl text-slate-400 text-sm md:text-base leading-relaxed">
            Try either platform live, free, right now — no signup form, no sales call required to
            take a first look.
          </p>
        </div>
      </section>

      {/* ---------- PRODUCT SECTIONS ---------- */}
      {products.map((p, idx) => (
        <Section key={p.key} background={idx % 2 === 0 ? 'white' : 'light'}>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-12 h-12 shrink-0 flex items-center justify-center bg-orange-600/10 text-orange-600 rounded-sm">
                  {p.icon}
                </span>
                <div>
                  <p className="text-orange-600 text-xs font-bold uppercase tracking-[0.2em]">
                    {p.tagline}
                  </p>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-slate-900">
                    {p.name}
                  </h2>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed text-base">{p.pitch}</p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button href={p.demoUrl} variant="primary" size="lg">
                  <Eye size={18} className="mr-2" /> Try Live Demo — Free
                </Button>
                <Button href="/contact" variant="outline" size="lg">
                  Talk to Us About Your Use Case
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5 grid sm:grid-cols-2 gap-5">
              {p.outcomes.map((o) => (
                <div key={o.title} className="bg-white border border-slate-200 p-6">
                  <div className="text-orange-600 mb-3">{o.icon}</div>
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-900 mb-2">
                    {o.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{o.body}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      ))}

      {/* ---------- WHY BUY FROM AN OPERATOR ---------- */}
      <Section background="dark">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Why It's Different
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            Built By Operators, Not a Software Vendor
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed">
            Most fleet and sales-intelligence tools are built by people who have never run a field
            operation. Ours were built inside one — under the same pressure, deadlines and margin
            constraints your team works under every day. That's the difference between a feature list
            and a tool that actually gets used.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { icon: <CheckCircle className="w-6 h-6" />, t: 'Proven In Our Own Operations', d: 'Running daily across our own yards and sales team — not a demo environment.' },
            { icon: <Users className="w-6 h-6" />, t: 'Built for Field Realities', d: "Designed around how oilfield equipment and sales cycles actually work, not a generic template." },
            { icon: <ShieldCheck className="w-6 h-6" />, t: 'Backed by EnergiPro Group', d: 'Support and ongoing development from the same team that built and runs it.' },
          ].map((f) => (
            <div key={f.t} className="border border-white/15 bg-white/5 p-6 text-center">
              <div className="text-orange-500 mb-3 flex justify-center">{f.icon}</div>
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-white mb-2">
                {f.t}
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ---------- CTA BANNER ---------- */}
      <section className="py-20 md:py-28 bg-slate-950 text-white border-t-2 border-orange-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            See It Running on Your Own Data
          </h2>
          <div className="h-1 w-16 bg-orange-600 mt-4 mx-auto" />
          <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            Take either platform for a free live test drive above, or talk to us about a walkthrough
            using your own fleet or sales data.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/contact" variant="primary" size="lg">
              Request a Walkthrough
            </Button>
            <Button href="/request-quote" variant="outline-light" size="lg">
              Request a Quote
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
